import { useMemo, useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";
import { getToday } from "@/services/Utils";

import CustomInputText from "@/components/desktop/form/input/input-text/CustomInputText";
import CustomInputSwitch from "@/components/desktop/form/input/input-switch/CustomInputSwitch";
import CustomBadge from "@/components/desktop/badge/CustomBadge";

import styles from "./integration-form.module.scss";

import IntegrationDatabases from "./IntegrationDatabases";

const IntegrationFields = ({ formState = {}, setFormState, formErrors = {}, handleExcel }) => {
	const dispatch = useDispatch();

	const isActive = Number(formState.active) === 1;

	const [viewProduction, setViewProduction] = useState(isActive);

	useEffect(() => {
		setViewProduction(Number(formState.active) === 1);
	}, [formState.active]);

	const isProduction = viewProduction;
	const prefix = isProduction ? "production_" : "";
	const envLabel = isProduction ? "Producción" : "Prueba";

	const fields = useMemo(
		() => [
			{ key: `${prefix}host`, label: "Host", placeholder: "" },
			{ key: `${prefix}company`, label: "Empresa", placeholder: "ID de empresa" },
			{ key: `${prefix}username`, label: "Usuario", placeholder: "" },
			{ key: `${prefix}password`, label: "Contraseña", type: "password", placeholder: "" },
		],
		[prefix],
	);

	const update = (key) => (val) => setFormState((prev) => ({ ...prev, [key]: val }));

	const handleToggleDatabase = (rowId, field) => {
		setFormState((prev) => {
			const nextDatabases = (prev.databases || []).map((db) => {
				const id = db.business_id ?? db.code;
				if (String(id) === String(rowId)) {
					return { ...db, [field]: db[field] ? 0 : 1 };
				}
				return db;
			});
			return { ...prev, databases: nextDatabases };
		});
	};

	const onExportExcel = (integration_db) => {
		const dbSuffix = integration_db ? ` DB ${integration_db}` : "";
		const filename = `Excel Integración ${formState.integration_name}${dbSuffix} ${getToday()}.xlsx`;

		dispatch({
			type: FETCHING_ADD_DOWNLOAD,
			payload: {
				file: {
					key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
					params: {
						integration_id: formState.integration_id,
						db_selected: integration_db || null,
					},
					show: true,
					url: "/admin/integrations/export_excel",
					name: filename,
					type: "excel",
					inExecution: false,
					inProgress: false,
					inChecked: false,
				},
			},
		});
	};

	return (
		<div>
			<div className={styles.envHeader}>
				<div className={styles.envTitles}>
					<div className={styles.envTitle}>
						Credenciales ({envLabel}){" "}
						{isActive ? (
							<CustomBadge variant="success" size="md">
								Integración activa en Producción
							</CustomBadge>
						) : (
							<CustomBadge variant="warning" size="md">
								Integración solo activa para pruebas
							</CustomBadge>
						)}
					</div>

					<div className={styles.envSubtitle}>
						Este switch solo cambia la <b>vista y configuración</b> entre <b>Prueba</b> y <b>Producción</b>. El estado real de la integración no se
						modifica aquí.
					</div>
				</div>

				<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
					<div className={styles.switchWrapper}>
						<CustomInputSwitch
							value={viewProduction}
							customStyles={{ width: "180px" }}
							onChange={setViewProduction}
							optionLeft="Prueba"
							optionRight="Producción"
						/>
					</div>
				</div>
			</div>

			<div className={styles.fieldsGrid}>
				{fields.map((f) => (
					<CustomInputText
						key={f.key}
						name={f.key}
						label={f.label}
						value={formState[f.key]}
						onChange={update(f.key)}
						placeholder={f.placeholder}
						type={f.type || "text"}
						error={formErrors[f.key]}
						helper=""
						size="md"
						col={6}
					/>
				))}
			</div>

			<IntegrationDatabases
				integration={formState}
				isProduction={isProduction}
				onToggle={handleToggleDatabase}
				onExportExcel={onExportExcel}
				onImportExcel={handleExcel}
			/>
		</div>
	);
};

export default IntegrationFields;
